/**
 * Utility functions for generating unique room codes
 */

/**
 * Generates a random 6-character alphanumeric room code
 * Format: XXXXXX (all uppercase)
 * Characters: A-Z and 0-9 (excluding easily confused: 0, O, I, 1)
 *
 * @returns {string} A 6-character room code
 */
export const generateRoomCode = () => {
  // Use characters that are easy to read and distinguish
  // Exclude: 0 (zero), O (letter O), I (letter I), 1 (one) to avoid confusion
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';

  for (let i = 0; i < 6; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    code += characters[randomIndex];
  }

  return code;
};

/**
 * Validates if a room code has the correct format
 *
 * @param {string} code - The room code to validate
 * @returns {boolean} True if valid format, false otherwise
 */
export const isValidRoomCode = (code) => {
  if (!code || typeof code !== 'string') {
    return false;
  }

  // Must be exactly 6 characters
  if (code.length !== 6) {
    return false;
  }

  // Must contain only valid characters (uppercase alphanumeric, excluding confusing chars)
  const validPattern = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/;
  return validPattern.test(code);
};

/**
 * Formats a room code for display (adds hyphen for readability)
 * Example: "ABC123" -> "ABC-123"
 *
 * @param {string} code - The room code
 * @returns {string} Formatted room code
 */
export const formatRoomCode = (code) => {
  if (!code || code.length !== 6) {
    return code;
  }

  return `${code.substring(0, 3)}-${code.substring(3, 6)}`;
};

/**
 * Removes formatting from a room code
 * Example: "ABC-123" -> "ABC123"
 *
 * @param {string} formattedCode - The formatted room code
 * @returns {string} Unformatted room code
 */
export const unformatRoomCode = (formattedCode) => {
  if (!formattedCode) {
    return '';
  }

  // Remove any hyphens, spaces, and convert to uppercase
  return formattedCode.replace(/[-\s]/g, '').toUpperCase();
};
